import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { css, cx } from 'styled-system/css';

import Tooltip from '@shaojiang/components/Tooltip';
import { StyleVariant } from '@shaojiang/types/common';

const SIZES = { xs: 12, sm: 16, md: 18, lg: 20, xl: 24, '2xl': 32 } as const;

const VARIANT_COLORS: Record<StyleVariant, string | undefined> = {
  [StyleVariant.Primary]: 'primary.9',
  [StyleVariant.Secondary]: 'fg.muted',
  [StyleVariant.Success]: 'success.main',
  [StyleVariant.Danger]: 'error.main',
  [StyleVariant.Error]: 'error.main',
  [StyleVariant.Warning]: 'warning.main',
  [StyleVariant.Info]: 'info.main',
  [StyleVariant.Highlight]: 'highlight.main',
  [StyleVariant.Default]: undefined,
};

interface Props {
  icon: LucideIcon;
  size?: keyof typeof SIZES;
  className?: string;
  /** Any CSS color, overrides the variant color */
  color?: string;
  /** White icon, e.g. on a selected (filled) menu item */
  contrast?: boolean;
  tooltip?: string;
  variant?: StyleVariant;
}

/** Lucide icon with the site's neutral icon color, variants and an optional tooltip. */
export default function AppIcon({
  icon: IconComponent,
  size = 'xl',
  className,
  color,
  contrast = false,
  tooltip = '',
  variant = StyleVariant.Default,
}: Props) {
  const iconNode = (
    <IconComponent
      size={SIZES[size]}
      style={color ? { color } : undefined}
      className={cx(
        css({
          display: 'inline-block',
          flexShrink: 0,
          color: contrast ? 'white' : (VARIANT_COLORS[variant] ?? 'fg.icon'),
        }),
        className,
      )}
    />
  );

  return tooltip ? (
    <Tooltip title={tooltip}>
      <span className={css({ display: 'inline-flex' })}>{iconNode}</span>
    </Tooltip>
  ) : (
    iconNode
  );
}
